<i18n>
{
  "en": {
    "terminal": {
      "serialnumber": "Serial number",
      "hardware": "Part number",
      "location": "Location",
      "credit": "Credit history (last 5)"
    },
    "titles": {
      "system": "System",
      "applications": "System Treatments",
      "applicators": "System Applicators",
      "swmodules": "System SW Modules",
      "regulatoryAuthority": "Regulatory Authority",
      "footer": "Press Continue to add credits or time"
    },
    "table": {
      "headers":{
        "application": "Application",
        "applicator": "Applicator",
        "cost_factor": "Cost Factor",
        "credit_per_shot": "Credit per Shot"
      }
    },
    "download": {
      "license": "Download latest configuration file",
      "credit": "Download latest credit file"
    },
    "buttons":{
      "continue": "Continue",
      "back": "Back",
      "setup": "Setup",
      "quit": "Quit",
      "ok": "OK"
    },
    "errors": {
      "selectionRequired": "Selection required",
      "applicationConfig" : "There was an issue with application configuration!",
      "applicatorConfig":"There was an issue with applicator configuration!",
      "minApplications":"At least one application must be selected!",
      "minApplicators":"At least one applicator must be selected!",
      "minSwModules": "At least one software module must be selected!"
    }
  },
  "da": {
    "terminal": {
      "serialnumber": "Serienummer",
      "hardware": "Part nummer",
      "location": "Lokation"
    },
    "titles": {
      "system": "System",
      "applications": "System behandlinger",
      "applicators": "System applikatorer",
      "swmodules": "System software moduler",
      "regulatoryAuthority": "Tilsynsmyndighed",
      "footer": "Tryk Fortsøt for at tilføje kredit eller tid"
    },
    "table": {
      "headers":{
        "application": "Applikation",
        "applicator": "Applikator",
        "cost_factor": "Pris faktor",
        "credit_per_shot": "Kredit per skud"
      }
    },
    "download": {
      "license": "Download seneste konfigurationsfil",
      "credit": "Download seneste kreditfil"
    },
    "buttons":{
      "continue": "Fortsæt",
      "back": "Tilbage",
      "setup": "Opsætning",
      "quit": "Afslut",
      "ok": "OK"
    },
    "errors": {
      "selectionRequired": "Valg pårkævet",
      "applicationConfig" : "Der var et problem med applikation konfigurationen!",
      "applicatorConfig":"Der var et problem med applikator konfigurationen!",
      "minApplications":"Mindst én applikation skal vælges!",
      "minApplicators":"Mindst én applikator skal vælges!",
      "minSwModules": "Mindst ét software modul skal vælges!"
    }
  }
}
</i18n>

<template>

  <layout>
    <b-container fluid>
      <loading :active.sync="isLoading" 
        :can-cancel="false" 
        :is-full-page="true"></loading>
      <b-row>
        <b-col lg="12" class="px-5"> 
          <system-details :system="system" type="config" :compact="true"/>
          
          <b-row >

            <!-- System Treatments / Applications -->
            <b-col lg="4" class="pr-lg-5">
              <b-row class="">
                <div class="ellipse-content-header" >
                  <h1 class="">{{ $t('titles.applications') }}</h1>
                </div>
              </b-row>
              
              <b-row>
                <b-table responsive sticky-header="37rem" striped hover :items="applications" small :fields="fields.applications">
                  <template v-slot:head(enabled)="data">
                    <b-form-checkbox @click.native.stop @change="toggleAllApplications()" v-model="selectAll.applications" size="sm"></b-form-checkbox>
                  </template>

                  <template v-slot:head(application)="data">
                    {{ $t('table.headers.application') }}
                  </template>

                  <template v-slot:head(credit_per_shot)="data">
                    {{ $t('table.headers.credit_per_shot') }}
                  </template>


                  <template v-slot:cell(enabled)="row">
                    <b-form-checkbox @click.native.stop @change="toggle(row, 'applications'); refresh(row)" size="sm" v-model="row.item.enabled" >
                    </b-form-checkbox>  
                  </template>

                  <template v-slot:cell(credit_per_shot)="row">
                    <div>
                      <b-form-select v-model="row.item.credit_per_shot" :options="types.perShotCreditCostType" @change="refresh(row)"/>
                      <span class="error-label force-up" v-if="row.error === true">{{$t('errors.selectionRequired')}}</span>  
                    </div>                
                  </template>
                  
                </b-table>
              </b-row>
            
            </b-col>


            <!-- System Applicators / Applicators -->
            <b-col lg="4" class="px-lg-5">
              <b-row>
                <div class="ellipse-content-header">
                  <h1 class="">{{ $t('titles.applicators') }}</h1>
                </div>
              </b-row>

              <b-row>
                <b-table responsive sticky-header="37rem" striped hover :items="applicators" small :fields="fields.applicators"	>
                  
                  <template v-slot:head(enabled)="data">
                    <b-form-checkbox @click.native.stop @change="toggleAllApplicators()" v-model="selectAll.applicators" size="sm"></b-form-checkbox>
                  </template>

                  <template v-slot:head(applicator)="data">
                    {{ $t('table.headers.applicator') }}
                  </template>

                  <template v-slot:head(cost_factor)="data">
                    {{ $t('table.headers.cost_factor') }}
                  </template>

                  <template v-slot:cell(enabled)="row">
                    <b-form-checkbox @click.native.stop @change="toggle(row, 'applicators'); refresh(row)" size="sm" v-model="row.item.enabled" >
                    </b-form-checkbox>  
                  </template>

                  <template v-slot:cell(cost_factor)="row">
                    <div>
                      <b-form-select v-model="row.item.cost_factor" :options="types.applicatorCostFactorType" @change="refresh(row)"/>
                      <span class="error-label force-up" v-if="row.error === true">{{$t('errors.selectionRequired')}}</span>  
                    </div>                
                  </template>

                </b-table>
              </b-row>

            </b-col>

            <!-- Software Modules -->
            <b-col lg="4" class="pl-lg-5">
              <b-row>
                <div class="ellipse-content-header">
                  <h1 class="">{{ $t('titles.swmodules') }}</h1>
                </div>
              </b-row>

              <b-row>
                <b-form-group>
                  <b-form-checkbox-group stacked v-model="selectedSwModules" :options="swModules" @change="refresh({item: { type:'sw' }})">
                  </b-form-checkbox-group>
                </b-form-group>
              </b-row>

              <b-row>
                <div class="ellipse-content-header">
                  <h1 class="">{{ $t('titles.regulatoryAuthority') }}</h1>
                </div>
              </b-row>

              <b-row>
                <b-col class="px-0 mb-4">
                  <b-form-select v-model="system.RegulatoryAuthority" :options="regulatoryAuthorities" />
                </b-col>
              </b-row>
              
            </b-col>

          </b-row>




          <b-row >
            <b-alert show dismissible variant="danger" v-for="error in errors.response.list" v-bind:key="error.Message">{{error.Message}}</b-alert>
        
            <b-alert variant="danger" :show="errors.applications">{{$t('errors.applicationConfig')}}</b-alert>
            <b-alert variant="danger" :show="errors.applicators"> {{$t('errors.applicatorConfig')}}</b-alert>

            <b-alert variant="danger" :show="errors.applicationSelection">{{$t('errors.minApplications')}}</b-alert>
            <b-alert variant="danger" :show="errors.applicatorSelection"> {{$t('errors.minApplicators')}}</b-alert>

            <b-alert variant="danger" :show="errors.swSelection">{{$t('errors.minSwModules')}}</b-alert>
          </b-row>

          <b-row><!-- System Buttoms -->
            <b-col md="12">
              <b-row>
                <b-button type="button" variant="outline-secondary" active-class="" class="min-width-button" :to="{ name: 'issue-system-config', params: { logosid: this.system.LogosID }}">{{$t('buttons.back')}}</b-button>
                <b-button type="button" variant="outline-secondary" active-class="" class="min-width-button ml-2" v-on:click="save">{{$t('buttons.continue')}}</b-button>
              </b-row>
            </b-col>
          </b-row>

        </b-col>
      </b-row>
    </b-container>


    <send-config-by-mail-modal ref="complete" type="config" :email="email" :system="system" :file="files.license" v-on:accept="redirectToCreditOverview" >
    </send-config-by-mail-modal>

  </layout>

</template>

<script>

import _ from 'lodash'
import axios from 'axios'
import FileSaver from 'file-saver'
import Loading from 'vue-loading-overlay';
import 'vue-loading-overlay/dist/vue-loading.min.css';

import Layout from './Layout'
import elg from '../api/elg.gateway.js'
import jwt from '../utils/jwt.js'

import SendConfigByMailModal from './modals/SendConfigByMailModal'
import SystemDetails from './subcomponent/SystemDetails'

export default {
  components: {
    Layout,
    Loading,

    SendConfigByMailModal,
    SystemDetails
  },

  data() {
    return {
      isLoading: false,

      fields: {
        applications: [
          {
            key: 'enabled',
            label: 'Application',
            class: 'table-checkbox-column',
            sortable: false
          },
          {
            key: 'application',
            label: '',
            tdClass: 'application_name_row'
          },
          {
            key: 'credit_per_shot',
            class: 'table-select-column'
          }
        ],
        applicators: [
          {
            key: 'enabled',
            label: 'Applicator',
            class: 'table-checkbox-column',
            sortable: false
          },
          {
            key: 'applicator',
            label: ''
          },
          {
            key: 'cost_factor',
            class: 'table-select-column'
          }
        ]
      },
      
      system: {},
      files: {
        license: undefined,
        credit: undefined
      },
      sendLicenseFileOnMail: false,

      errors: {
        response: {
          list: [],
          message: undefined
        },
        swSelection: false,
        applicationSelection: false,
        applicatorSelection: false,

        applications: false,
        applicators: false
      },

      types: {
        applicatorCostFactorType: [],
        perShotCreditCostType: []
      },

      applications: [],
      applicators: [],
      swModules: [],
      selectedSwModules: [],
      regulatoryAuthorities: [],

      selectAll: {
        applications: false,
        applicators: false
      }
    };
  },

  methods: {
    refresh(row){
      console.log("Refresh: %j", row);
      if(row !== undefined){
        this.$set(row.item, 'error', false);
        if( row.item.type === 'application' ){
          this.errors.application = false;
          if( this.errors.applicationSelection === true && row.item.enabled === true){
            this.errors.applicationSelection = false;
          }
        }else if( row.item.type === 'applicator' ){
          this.errors.applicators = false;
          if( this.errors.applicatorSelection === true && row.item.enabled === true){
            this.errors.applicatorSelection = false;
          }
        }else if( row.item.type === 'sw' ){
          this.errors.swSelection = false;
        }
      }
    },
    validate(){
      /*
        Validate that at least 1 application has been chosen
                 that at least 1 applicator has been chosen
                 that at least 1 SW module has been chosen
      */
      var state = true;

      if( _.find(this.applications, function(a){ return a.enabled === true; }) === undefined ){
       this.errors.applicationSelection = true;
       state = false;
      }
      if( _.find(this.applicators, function(a){ return a.enabled === true; }) === undefined ){
        this.errors.applicatorSelection = true;
        state = false;
      }

      /*if( this.selectedSwModules.length === 0 ){
        this.errors.swSelection = true;
        state = false;
      }*/

      for( var i = 0; i < this.applications.length; i++ ){
        var application = this.applications[i];
        if( application.enabled === true && application.credit_per_shot === undefined ){
          this.$set(this.applications[i], 'error', true);
          this.errors.applications = true;
          state = false;
        }
      }

      for(var i = 0; i < this.applicators.length; i++){
        if( this.applicators[i].enabled === true && this.applicators[i].cost_factor === undefined ){
          this.$set(this.applicators[i], 'error', true);
          this.errors.applicators = true;
          state = false;
        }
      }

      return state;
    },
    // Binding methods
    toggle(row, type){
      row.enabled = !row.enabled;

      if(this.selectAll[type] === true && row.enabled === false){
        this.selectAll[type] = false;
      }

    },
    setCostPerShot(row){
      row.credit_per_shot = row.credit_per_shot
    },
    setCreditPerEnegery(row){
      row.credit_per_energy = row.credit_per_energy
    },
    setDemoPeriod(row){
      row.demo_period = row.demo_period
    },

    toggleAllApplications(){
      this.selectAll.applications = !this.selectAll.applications;
      for(var i = 0; i < this.applications.length; i++ ){
        this.$set(this.applications[i], 'enabled', this.selectAll.applications);
      }
    },
    toggleAllApplicators(){
      this.selectAll.applicators = !this.selectAll.applicators;
      for(var i = 0; i < this.applicators.length; i++ ){
        this.$set(this.applicators[i], 'enabled', this.selectAll.applicators);
      }
    },

    // Actual methods
    save() {
      if(!this.validate())
        return; 
      
      var data = {
        LogosID: this.system.LogosID,
        IssueInformation: {
          LicenseType: 'License',
          LicenseActionType: 'Replace existing'
        },
        SystemInformation: {
          SerialNumber: this.system.SerialNumber,
          InstallationId: this.system.InstallationID,
          SystemPartNumber: this.system.SystemPartNumber,
          SystemDongle: this.system.SystemDongle,
          RegulatoryAuthority: this.system.RegulatoryAuthority
        },
        Applicators: [],
        Applications: [],
        SystemSoftwareModules: []
      }

      for( var i = 0; i < this.applicators.length; i++ ){
        var applicator = this.applicators[i];
        if(applicator.enabled){
          data.Applicators.push({
            Name: applicator.applicator,
            CostFactor: applicator.cost_factor
          });
        }
      }

      for( var i = 0; i < this.applications.length; i++){
        var application = this.applications[i];
        
        if(application.enabled){
          data.Applications.push({
            Name: application.application,
            CreditsPerShot: application.credit_per_shot
          });
        }
      }

      for( var i = 0; i < this.selectedSwModules.length; i++ ){
        data.SystemSoftwareModules.push({
          Name: this.selectedSwModules[i]
        })
      }

      this.isLoading = true;

      elg()
      .post('/api/v1/license', data)
      .then( res => {
        this.isLoading = false;
        this.files.license = res.data;
        this.$refs.complete.show();
      })
      .catch( err => {
        this.isLoading = false;

        this.errors.response.list.push(err.response.data);
        for( var i = 0; i < err.response.data.Errors.length; i++){
          this.errors.response.list.push(err.response.data.Errors[i]);
        }

      });
    },
    confirm(){
      if(this.sendLicenseFileOnMail){
        var data = {
          Recipient: this.email,
          Subject: "New Ellipse license file",
          Body: "A new Ellipse license file has been generated."
        };
        
        elg()
        .post(`/api/v1/files/${this.files.license.FileID}/mail`, data)
        .then( res => {
          this.$refs.complete.hide();
        });
      } else {
        this.$refs.complete.hide();
      }
    },
    downloadLicenseFile(){
      elg()
      .get('/api/v1/files/' + this.files.license.FileID)
      .then((response) => {
        FileSaver.saveAs(new Blob([response.data]), this.files.license.Filename);
      });
    },
    downloadCreditFile(){
      elg()
      .get('/api/v1/files/' + this.files.credit.FileID)
      .then((response) => {
        FileSaver.saveAs(new Blob([response.data]), this.files.credit.Filename);
      });
    },
    redirectToCreditOverview() {
      this.$router.push({
        name: 'system-config-overview',
        params:{
          logosid: this.$route.params.logosid
        }
      })
    }
  },

  mounted() {
   
    elg()
    .get(`/api/v1/systems/${this.$route.params.logosid}`)
    .then( res => {
      this.system = res.data;

      return axios.all([ 
        elg().get('/api/v1/license/config'),
        elg().get(`/api/v1/systems/${this.$route.params.logosid}/configuration${ this.$route.query.country !== undefined ? ( "?country=" + this.$route.query.country ) : "" }`)
      ]);
    })
    .then( axios.spread( (config, parameters) => {
      var applicationsList = config.data.ApplicationsType.Enum;
      for( var i = 0; i < applicationsList.length; i++){
        var enabled = false;
        var credit_per_shot = undefined

        // Check if previous configuration or template has the System Treatment / Application selected
        var param = _.find(parameters.data.Applications, p => {
          return p.Name === applicationsList[i]
        })

        if(param !== undefined){
          enabled = true;
          credit_per_shot = parseFloat(param.CreditsPerShot);
        }

        this.applications.push({
          enabled: enabled,
          application: applicationsList[i],
          credit_per_shot: credit_per_shot,
          //credit_per_energy: 0,
          //demo_period: 0
          type: 'application'
        })
      }

      var applicatorList = config.data.ApplicatorsType.Enum;
      for( var i = 0; i < applicatorList.length; i++){
        var enabled = false;
        var cost_factor = undefined

        // Check if previous configuration or template has the Applicator selected
        var param = _.find(parameters.data.Applicators, p => {
          return p.Name === applicatorList[i]
        })

        if(param !== undefined){
          enabled = true;
          cost_factor = parseFloat(param.CostFactor);
        }

        this.applicators.push({
          enabled: enabled,
          applicator: applicatorList[i],
          cost_factor: cost_factor,
          type: 'applicator'
        })
      }

      var swModuleList = config.data.SoftwareModulesType.Enum;
      for( var i = 0; i < swModuleList.length; i++){
        this.swModules.push({
          text: swModuleList[i],
          value: swModuleList[i]
        })

        // Check if the previous configuration or template has the software selected
        if(_.find(parameters.data.SystemSoftwareModules, sw => { return sw.Name === swModuleList[i]; }) ){
          this.selectedSwModules.push(swModuleList[i]);
        }
      }

      this.regulatoryAuthorities = config.data.RegulatoryAuthorityTypes.Enum; 
      var regulatoryAuthority = _.find(parameters.data.Misc, p => {
        return p.Name === "RegulatoryAuthority";
      })
      this.system.RegulatoryAuthority = regulatoryAuthority.Value;

      var applicatorCostFactorType = config.data.ApplicatorCostFactorType.Enum;
      for( var i = 0; i < applicatorCostFactorType.length; i++ ){
        this.types.applicatorCostFactorType.push({
          value: parseFloat(applicatorCostFactorType[i]),
          text: applicatorCostFactorType[i]
        })
      }

      var perShotCreditCostType = config.data.PerShotCreditCostType.Enum;
      for( var i = 0; i < perShotCreditCostType.length; i++ ){
        this.types.perShotCreditCostType.push({
          value: parseFloat(perShotCreditCostType[i]),
          text: perShotCreditCostType[i]
        })
      }
      for(var i = 0; i < this.applications.length; i++){
        if(this.applications[i].credit_per_shot === undefined || !_.find(this.types.perShotCreditCostType, s => { return s.value === this.applications[i].credit_per_shot; } ) )
          this.applications[i].credit_per_shot = this.types.perShotCreditCostType[0].value;
      }
      for(var i = 0; i < this.applicators.length; i++){
         if(this.applicators[i].cost_factor === undefined || !_.find(this.types.applicatorCostFactorType, s => { return s.value === this.applicators[i].cost_factor; } ) )
          this.applicators[i].cost_factor = this.types.applicatorCostFactorType[0].value;
      }

      // Check all state update
      if( _.every(this.applications, a => { return a.enabled; }) ){
        this.selectAll.applications = true;
      }
      if( _.every(this.applicators, a => { return a.enabled; }) ){
        this.selectAll.applicators = true;
      }

    }));

    elg()
    .get(`/api/v1/system/${this.$route.params.logosid}/files?Latest=True`)
    .then( res => {
      var license = _.find(res.data, function (o){
        return o.FileTypeID === 100;
      });

      var credit = _.find(res.data, function (o){
        return o.FileTypeID === 200;
      });

      if(license !== undefined){
        this.files.license = license;
      }

      if(credit !== undefined){
        this.files.credit = credit;
      }
    });
  },

  computed: {
    logosid () {
      return this.$route.params.logosid
    },
    email(){
      return jwt.getEmail();
    }
  }
};
</script>

<style scoped>

  @media (min-width: 768px) {
    .table-max-height {
      max-height: 500rem;
    }    
  }

  .pretty-padding {
    padding-bottom: 20px;
  }
  .button-spacing {
    margin-right: 35px;
  }

  tbody select {
    margin: 0;
  }

  tbody > tr > td > select {
    margin: 0 !important; /* Ugh */
  }

  .application_name_row {
    vertical-align : baseline;
  }

</style>



// WEBPACK FOOTER //
// WebApp/components/IssueAdvancedSystemConfig.vue